import React, { useEffect, useState } from "react";

// region External libraries
import { useLazyQuery, useMutation } from "@apollo/client";
import { useToast } from "@npm-telluria-tecnologia/telluria-ui";
import _ from "lodash";
import { matchesSubstringIgnoreCaseAndAccents } from "@npm-telluria-tecnologia/telluria-utils";
import { useMsal } from "@azure/msal-react";
// endregion External libraries
// region Languages
import { FilterMessages, GlobalMessages, TenantSelectionMessages } from "src/languages/interfaces";
// endregion Languages
// region Atoms
import ActionDialog from "@atoms/ActionDialog";
import Loading from "@atoms/Loading";
import PageTitle from "@atoms/PageTitle";
import SearchField from "@atoms/SearchField";
import TenantSelectionCard from "@atoms/TenantSelectionCard";
// endregion Atoms
// region Hooks
import useTranslation from "src/languages/useTranslation";
// endregion Hooks
// region Store
import {
  ECommandResultStatus,
  TenantQueriesGetAllWithPaginationArgs,
  TenantType,
  UserMutationsUpdateUserTenantArgs
} from "src/store/graphql/helpers/types.graphql";
import {
  tenantAllWithPagination,
  TTenantAllWithPaginationQueries
} from "src/store/graphql/groups/tenant/query.graphql";
import { TUpdateUserTenantMutations, updateUserTenant } from "src/store/graphql/groups/user/mutation.graphql";
// endregion Store

import * as Styled from "./styles";

const TenantSelection: React.FC = () => {
  // region Queries
  const [QueryGetTenantAllWithPagination] = useLazyQuery<TTenantAllWithPaginationQueries,
    TenantQueriesGetAllWithPaginationArgs>(
      tenantAllWithPagination([
        "id", "name"
      ])
    );
  // endregion Queries
  const [MutationUpdateUserTenant] = useMutation<TUpdateUserTenantMutations,
    UserMutationsUpdateUserTenantArgs>(updateUserTenant());

  // region Hooks
  const { t } = useTranslation();
  const { addToast } = useToast();
  const { instance } = useMsal();
  // endregion Hooks
  // region States
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [tenants, setTenants] = useState<TenantType[]>([]);
  const [currentTenant, setCurrentTenant] = useState<TenantType>({} as TenantType);
  const [selectedTenantId, setSelectedTenantId] = useState<string | null>(null);
  const [displayedTenants, setDisplayedTenants] = useState<TenantType[]>([]);
  // endregion States

  // region Functions

  const sortedTenants = (tenantsToSort: TenantType[]) => tenantsToSort.sort((a, b) => a.name.localeCompare(b.name));

  const filterDataOnScreen = (termForFilteringDataOnScreen: string) => {
    if (!termForFilteringDataOnScreen) {
      setDisplayedTenants(sortedTenants(tenants));

      return;
    }

    const match = (propValue: string) => {
      const formattedPropValue = propValue ?? "";

      return matchesSubstringIgnoreCaseAndAccents(formattedPropValue.toString(), termForFilteringDataOnScreen);
    };

    const cloneTenants = _.cloneDeep(tenants);

    const filteredTenants = cloneTenants.filter((tenant) => (match(tenant.name)));

    setDisplayedTenants(sortedTenants(filteredTenants));
  };

  const getTenants = async () => {
    try {
      const query = await QueryGetTenantAllWithPagination();

      const response = query.data?.tenant.getAllWithPagination;

      if (response?.status !== ECommandResultStatus.SUCCESS) return [] as TenantType[];

      return response.result as TenantType[];
    } catch (error) {
      addToast({ type: "error", title: "Internal client error", description: error.message });

      return [] as TenantType[];
    }
  };

  const mutationUpdateUserTenant = async (idB2C: string, tenantId: string) => {
    try {
      setIsLoading(true);

      const query = await MutationUpdateUserTenant({ variables: { idB2C, tenantId } });

      const response = query.data?.user.updateUserTenant;

      if (response?.status !== ECommandResultStatus.SUCCESS) return false;

      return true;

    } catch (error) {
      addToast({ type: "error", title: "Internal client error", description: error.message });

      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const handleEndOfProcess = async () => {
    const currentUserIdB2C = instance.getActiveAccount()?.idTokenClaims?.oid;

    const isUpdateSuccessfull = await mutationUpdateUserTenant(currentUserIdB2C ?? "", selectedTenantId ?? "");

    if (isUpdateSuccessfull) instance.logout();
  };
  // endregion functions

  useEffect(() => {
    (async () => {
      setIsLoading(true);

      let currentTenant = {} as TenantType;
      const filteredTenants = [] as TenantType[];
      const activeAccount = instance.getActiveAccount();

      const tenants = await getTenants();

      for (const tenant of tenants) {
        if (tenant.id === activeAccount?.idTokenClaims?.extension_TenantId) {
          currentTenant = tenant;
        } else {
          filteredTenants.push(tenant);
        }
      }

      setCurrentTenant(currentTenant);
      setTenants(sortedTenants(filteredTenants));
      setDisplayedTenants(sortedTenants(filteredTenants));

      setIsLoading(false);
    })();

    return () => {
      setTenants([]);
      setCurrentTenant({} as TenantType);
      setDisplayedTenants([]);
    };
  }, []);

  return (
    <Styled.Container>
      <Loading loading={isLoading} />
      <Styled.Header>
        <PageTitle>{t(TenantSelectionMessages.title)}</PageTitle>
        <Styled.CurrentTenant>
          {t(TenantSelectionMessages.currentTenant, { currentTenantName: currentTenant.name })}
        </Styled.CurrentTenant>
      </Styled.Header>
      <Styled.Board>
        <SearchField
          fieldLabel={t(FilterMessages.labelFilterDataOnScreen)}
          onSearchClick={filterDataOnScreen}
          onClearClick={() => filterDataOnScreen("")}
        />
        <Styled.TenantsBoard>
          {displayedTenants.map((tenant) => (
            <TenantSelectionCard key={tenant.id} tenant={tenant} onClick={(tenantId) => setSelectedTenantId(tenantId)} />
          ))}
        </Styled.TenantsBoard>
      </Styled.Board>
      <ActionDialog
        open={!!selectedTenantId}
        onClose={() => setSelectedTenantId(null)}
        title={t(TenantSelectionMessages.confirmTenantSelectionModalTitle)}
        actions={[
          { text: t(GlobalMessages.buttonCancel), action: () => { setSelectedTenantId(null); } },
          { text: t(GlobalMessages.buttonConfirm), action: () => { handleEndOfProcess(); } }
        ]}
      >
        {t(TenantSelectionMessages.confirmTenantSelectionModalContent)}
      </ActionDialog>
    </Styled.Container>
  );
};

export default TenantSelection;